import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle, Circle, AlertCircle, Book, Wrench, HelpCircle, Loader2 } from 'lucide-react';
import { GenerationSettings, TopicSuggestion } from '../types/index.ts';
import { apiV1Url } from '../lib/apiBaseUrl.ts';
import { creatorFetch } from '../services/creatorFetch.ts';
import { ensurePfReady } from '../services/ensurePfReady.ts';

interface TopicSelectionProps {
  documentId: string;
  productName: string;
  onTopicsSelected: (selectedTopics: TopicSuggestion[]) => void;
  initialTopics?: TopicSuggestion[];
  initialSelectedTopicIds?: string[];
  onCacheState?: (state: { topics: TopicSuggestion[]; selectedTopicIds: string[] }) => void;
  onBack: () => void;
  generationSettings: GenerationSettings;
  onGenerationSettingsChange?: (next: GenerationSettings) => void;
  sourceKind?: 'manual' | 'webpage' | 'search' | 'product_walkthrough';
  isLoading?: boolean;
}

interface DocumentSection {
  sectionId: string;
  title: string;
  type?: string;
  preview?: string;
}

type SourceLangCode = 'de' | 'en';

const OUTPUT_LANG_BY_CODE: Record<SourceLangCode, string> = {
  de: 'German',
  en: 'English'
};

const LANG_LABEL: Record<SourceLangCode, string> = {
  de: 'German',
  en: 'English'
};

const toSourceLangCode = (value?: string | null): SourceLangCode | null => {
  if (!value || value === 'auto') return null;
  const normalized = value.toLowerCase();
  if (normalized === 'de' || normalized.startsWith('de-') || normalized === 'german') return 'de';
  if (normalized === 'en' || normalized.startsWith('en-') || normalized === 'english') return 'en';
  return null;
};

const TopicSelection: React.FC<TopicSelectionProps> = ({
  documentId,
  productName,
  onTopicsSelected,
  initialTopics,
  initialSelectedTopicIds,
  onCacheState,
  onBack,
  generationSettings,
  onGenerationSettingsChange,
  sourceKind,
  isLoading = false
}) => {
  const hasCachedTopics = Boolean(initialTopics && initialTopics.length > 0);
  const [phase, setPhase] = useState<'language' | 'topics'>(hasCachedTopics ? 'topics' : 'language');
  const [sourceLangCode, setSourceLangCode] = useState<SourceLangCode>(
    toSourceLangCode(generationSettings.preferredLanguage) || 'en'
  );
  const [languageHint, setLanguageHint] = useState<{
    confidence: string;
    reason: string;
    detectedLanguages: string[];
  } | null>(null);
  const [languageLoading, setLanguageLoading] = useState(!hasCachedTopics);
  const [topics, setTopics] = useState<TopicSuggestion[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [noTopicsFound, setNoTopicsFound] = useState(false);
  const [loadingStage, setLoadingStage] = useState<'preparing' | 'analyzing' | 'processing' | 'finalizing'>('preparing');
  const [warmupMessage, setWarmupMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [sections, setSections] = useState<DocumentSection[]>([]);
  const [documentTextHash, setDocumentTextHash] = useState<string | null>(null);
  const [includedSectionIds, setIncludedSectionIds] = useState<Set<string>>(new Set());
  const [excludedSectionIds, setExcludedSectionIds] = useState<Set<string>>(new Set());
  const [savingSelection, setSavingSelection] = useState(false);
  const [selectionId, setSelectionId] = useState<string | null>(null);
  const lastAnalyzeKeyRef = useRef<string | null>(null);
  const inFlightRef = useRef<boolean>(false);

  const selectionStorageKey = `doc_selection_${documentId}`;
  const intentLabel = generationSettings.template === 'quickstart' ? 'Quickstart' : 'Product Guide';
  const sourceLabel = sourceKind === 'webpage'
    ? 'webpage'
    : sourceKind === 'search'
      ? 'search'
      : 'manual';
  const contextSummary = generationSettings.template === 'quickstart'
    ? 'Topic extraction is prioritizing first success, setup, and first-use tasks.'
    : 'Topic extraction is aiming for broader operational coverage across the product lifecycle.';

  useEffect(() => {
    const existing = localStorage.getItem(selectionStorageKey);
    if (existing) setSelectionId(existing);
  }, [selectionStorageKey]);

  const openSourceModal = async () => {
    setShowSourceModal(true);
    try {
      const qs = new URLSearchParams({
        skip_disclaimers: String(generationSettings.filters.skipDisclaimers),
        skip_toc: String(generationSettings.filters.skipToc)
      });
      const resp = await creatorFetch(apiV1Url(`/api/v1/documents/${documentId}/sections?${qs.toString()}`));
      if (!resp.ok) throw new Error(await resp.text());
      const data = await resp.json();
      const payload = data.data || {};
      setSections(payload.sections || []);
      setDocumentTextHash(payload.documentTextHash || null);
      const defSel = payload.defaultSelection || {};
      setIncludedSectionIds(new Set(defSel.includedSectionIds || []));
      setExcludedSectionIds(new Set(defSel.excludedSectionIds || []));
    } catch (e) {
      // keep modal open but show error via main error slot
      setError(e instanceof Error ? e.message : 'Failed to load document sections');
    }
  };

  const toggleSection = (sectionId: string) => {
    const nextIncluded = new Set(includedSectionIds);
    const nextExcluded = new Set(excludedSectionIds);
    if (nextIncluded.has(sectionId)) {
      nextIncluded.delete(sectionId);
      nextExcluded.add(sectionId);
    } else {
      nextIncluded.add(sectionId);
      nextExcluded.delete(sectionId);
    }
    setIncludedSectionIds(nextIncluded);
    setExcludedSectionIds(nextExcluded);
  };

  const saveSelection = async () => {
    if (!documentTextHash) return;
    setSavingSelection(true);
    try {
      const resp = await creatorFetch(apiV1Url(`/api/v1/documents/${documentId}/selections`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentTextHash,
          includedSectionIds: Array.from(includedSectionIds),
          excludedSectionIds: Array.from(excludedSectionIds),
          settingsSnapshot: generationSettings
        })
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt);
      }
      const data = await resp.json();
      const sel = data.data?.selection;
      if (sel?.selectionId) {
        setSelectionId(sel.selectionId);
        localStorage.setItem(selectionStorageKey, sel.selectionId);
      }
      setShowSourceModal(false);
      // Re-run topic analysis with persisted selectionId (best for refresh/resume)
      analyzeTopics(sel?.selectionId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save selection');
    } finally {
      setSavingSelection(false);
    }
  };

  const analyzeTopics = useCallback(async (forcedSelectionId?: string, langOverride?: SourceLangCode) => {
    try {
      // Protect against accidental passing of a React event (e.g. onClick={analyzeTopics})
      const safeSelectionId = typeof forcedSelectionId === 'string' ? forcedSelectionId : undefined;
      const langCode = langOverride || sourceLangCode;
      const outputLang = OUTPUT_LANG_BY_CODE[langCode];
      // React 18 StrictMode runs effects twice in dev; dedupe identical requests to avoid
      // double LLM calls (which can yield slightly different wording).
      const analyzeKey = JSON.stringify({
        documentId,
        productName,
        template: generationSettings.template,
        style: generationSettings.style,
        preferredLanguage: langCode,
        outputLang,
        maxTopics: generationSettings.maxTopics,
        filters: generationSettings.filters,
        selectionId: safeSelectionId || selectionId || null
      });
      if (inFlightRef.current) return;
      if (lastAnalyzeKeyRef.current === analyzeKey) return;
      lastAnalyzeKeyRef.current = analyzeKey;
      inFlightRef.current = true;

      setPhase('topics');
      setLoading(true);
      setNoTopicsFound(false);
      setLoadingStage('preparing');
      setError(null);
      setWarmupMessage(null);

      // Warm up instruction-flow services (doc2simpi) before kicking off topic extraction.
      await ensurePfReady({
        maxWaitMs: 45000,
        onProgress: (msg) => setWarmupMessage(msg)
      });

      // Next: actually call the backend (LLM work happens server-side)
      setLoadingStage('analyzing');

      const requestBody = {
        document_id: documentId,
        product_name: productName,
        template: generationSettings.template,
        style: generationSettings.style,
        preferred_locale: langCode,
        output_lang: outputLang,
        max_topics: generationSettings.maxTopics,
        skip_disclaimers: generationSettings.filters.skipDisclaimers,
        skip_duplicate_languages: generationSettings.filters.skipDuplicateLanguages,
        skip_toc: generationSettings.filters.skipToc,
        selection_id: safeSelectionId || selectionId || undefined
      };
      
      const response = await creatorFetch(apiV1Url('/api/v1/analyze-topics'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        // Some ACA cold-start / gateway behaviors can surface as transient 402/502/503/504.
        if ([402, 502, 503, 504].includes(response.status)) {
          setWarmupMessage('Starting AI processor… please wait and we’ll retry automatically.');
          await ensurePfReady({ maxWaitMs: 45000, onProgress: (msg) => setWarmupMessage(msg) });
          const retry = await creatorFetch(apiV1Url('/api/v1/analyze-topics'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
          });
          if (!retry.ok) {
            const retryText = await retry.text();
            throw new Error(`Failed to analyze topics: ${retry.status} ${retryText}`);
          }
          setLoadingStage('processing');
          const data = await retry.json();
          if (data.success) {
            const nextTopics: TopicSuggestion[] = data.data.topics || [];
            setTopics(nextTopics);
            setNoTopicsFound(nextTopics.length === 0);

            if (nextTopics.length > 0) {
              const nextSelected = new Set([nextTopics[0].id]);
              setSelectedTopics(nextSelected);
              onCacheState?.({ topics: nextTopics, selectedTopicIds: Array.from(nextSelected) });
            } else {
              setSelectedTopics(new Set());
              onCacheState?.({ topics: nextTopics, selectedTopicIds: [] });
            }
            setLoadingStage('finalizing');
            return;
          }
          throw new Error(data.message || 'Failed to analyze topics');
        }
        throw new Error(`Failed to analyze topics: ${response.status} ${errorText}`);
      }

      setLoadingStage('processing');
      const data = await response.json();
      
      if (data.success) {
        const nextTopics: TopicSuggestion[] = data.data.topics || [];
        setTopics(nextTopics);
        setNoTopicsFound(nextTopics.length === 0);

        // Pre-select the first topic
        if (nextTopics.length > 0) {
          const nextSelected = new Set([nextTopics[0].id]);
          setSelectedTopics(nextSelected);
          onCacheState?.({ topics: nextTopics, selectedTopicIds: Array.from(nextSelected) });
        } else {
          setSelectedTopics(new Set());
          onCacheState?.({ topics: nextTopics, selectedTopicIds: [] });
        }
        setLoadingStage('finalizing');
      } else {
        throw new Error(data.message || 'Failed to analyze topics');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [documentId, productName, generationSettings, selectionId, onCacheState, sourceLangCode]);

  useEffect(() => {
    // When navigating back from template/settings, we want to show the previous topic list
    // without re-triggering the (potentially expensive) analyze call.
    if (initialTopics && initialTopics.length > 0) {
      setTopics(initialTopics);
      setNoTopicsFound(false);
      const restored = new Set(initialSelectedTopicIds || []);
      setSelectedTopics(restored);
      setPhase('topics');
      setLoading(false);
      setLanguageLoading(false);
      return;
    }

    let cancelled = false;
    const loadLanguageHint = async () => {
      setLanguageLoading(true);
      setError(null);
      try {
        const explicit = toSourceLangCode(generationSettings.preferredLanguage);
        if (explicit) {
          if (!cancelled) {
            setSourceLangCode(explicit);
            setLanguageHint(null);
          }
          return;
        }
        const resp = await creatorFetch(apiV1Url(`/api/v1/documents/${documentId}/language-hint`));
        if (!resp.ok) throw new Error(await resp.text());
        const data = await resp.json();
        const payload = data.data || {};
        const detected = toSourceLangCode(payload.langCode) || toSourceLangCode(payload.outputLang) || 'en';
        if (!cancelled) {
          setSourceLangCode(detected);
          setLanguageHint({
            confidence: payload.confidence || 'low',
            reason: payload.reason || '',
            detectedLanguages: payload.detectedLanguages || []
          });
        }
      } catch (e) {
        if (!cancelled) {
          setSourceLangCode('en');
          setLanguageHint({
            confidence: 'low',
            reason: 'Could not detect language; defaulting to English.',
            detectedLanguages: []
          });
        }
      } finally {
        if (!cancelled) setLanguageLoading(false);
      }
    };
    loadLanguageHint();
    return () => {
      cancelled = true;
    };
  }, [documentId, generationSettings.preferredLanguage, initialTopics, initialSelectedTopicIds]);

  const persistLanguageAndExtract = () => {
    const nextSettings = {
      ...generationSettings,
      preferredLanguage: sourceLangCode
    };
    onGenerationSettingsChange?.(nextSettings);
    lastAnalyzeKeyRef.current = null;
    analyzeTopics(undefined, sourceLangCode);
  };

  const toggleTopic = (topicId: string) => {
    const newSelected = new Set(selectedTopics);
    if (newSelected.has(topicId)) {
      newSelected.delete(topicId);
    } else {
      newSelected.add(topicId);
    }
    setSelectedTopics(newSelected);
    onCacheState?.({ topics, selectedTopicIds: Array.from(newSelected) });
  };

  const handleContinue = () => {
    const selected = topics.filter(topic => selectedTopics.has(topic.id));
    onCacheState?.({ topics, selectedTopicIds: Array.from(selectedTopics) });
    onTopicsSelected(selected);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'setup':
        return <Book className="w-4 h-4" />;
      case 'maintenance':
        return <Wrench className="w-4 h-4" />;
      case 'troubleshooting':
        return <HelpCircle className="w-4 h-4" />;
      default:
        return <Book className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'setup':
        return 'bg-blue-100 text-blue-800';
      case 'maintenance':
        return 'bg-purple-100 text-purple-800';
      case 'troubleshooting':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryDisplayName = (category: string) => {
    switch (category) {
      case 'setup':
        return 'Setup & Installation';
      case 'operation':
        return 'Operation & Usage';
      case 'maintenance':
        return 'Maintenance & Care';
      case 'troubleshooting':
        return 'Troubleshooting & Support';
      default:
        return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };

  const groupTopicsByCategory = (topics: TopicSuggestion[]) => {
    const grouped = topics.reduce((acc, topic) => {
      if (!acc[topic.category]) {
        acc[topic.category] = [];
      }
      acc[topic.category].push(topic);
      return acc;
    }, {} as Record<string, TopicSuggestion[]>);

    // Sort categories in a logical order
    const categoryOrder = ['setup', 'operation', 'maintenance', 'troubleshooting'];
    const sortedGrouped: Record<string, TopicSuggestion[]> = {};
    
    categoryOrder.forEach(category => {
      if (grouped[category]) {
        sortedGrouped[category] = grouped[category];
      }
    });

    // Add any remaining categories
    Object.keys(grouped).forEach(category => {
      if (!categoryOrder.includes(category)) {
        sortedGrouped[category] = grouped[category];
      }
    });

    return sortedGrouped;
  };

  if (phase === 'language') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Source language</h2>
          <p className="mx-auto max-w-2xl text-sm text-gray-600">
            Manuals are often multi-language. Choose the instructional language to extract
            topics from before analysis starts.
          </p>
          {productName && (
            <p className="text-sm text-gray-500 mt-1">Product: {productName}</p>
          )}
        </div>

        <div className="mx-auto max-w-lg rounded-lg border border-gray-200 bg-white p-5 space-y-4">
          {languageLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Detecting language from the uploaded manual…
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">Instructional language</label>
                <select
                  value={sourceLangCode}
                  onChange={(e) => setSourceLangCode(e.target.value as SourceLangCode)}
                  className="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="de">German</option>
                  <option value="en">English</option>
                </select>
              </div>
              {languageHint && (
                <p className="text-xs text-gray-500">
                  Suggested from the manual
                  {languageHint.confidence ? ` (${languageHint.confidence} confidence)` : ''}.
                  {languageHint.reason ? ` ${languageHint.reason}` : ''}
                </p>
              )}
              <p className="text-xs text-gray-500">
                Topics and evidence will be generated in {LANG_LABEL[sourceLangCode]} so they stay
                grounded in that language’s instructional text.
              </p>
            </>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <button
              type="button"
              onClick={onBack}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={persistLanguageAndExtract}
              disabled={languageLoading || isLoading}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Extract topics
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    const stageText: Record<typeof loadingStage, string> = {
      preparing: 'Preparing analysis request…',
      analyzing: 'Asking the AI to propose guide topics…',
      processing: 'Processing and ranking suggested topics…',
      finalizing: 'Finalizing topic list…'
    };
    const steps: Array<{ key: typeof loadingStage; label: string }> = [
      { key: 'preparing', label: 'Preparing request' },
      { key: 'analyzing', label: 'Extracting topics (LLM)' },
      { key: 'processing', label: 'Processing results' },
      { key: 'finalizing', label: 'Finalizing' }
    ];
    const currentIdx = steps.findIndex(s => s.key === loadingStage);

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Analyzing Content</h2>
          <p className="text-gray-600">{stageText[loadingStage]}</p>
          {warmupMessage && loadingStage === 'preparing' && (
            <p className="text-sm text-gray-500 mt-2">{warmupMessage}</p>
          )}
          {productName && (
            <p className="text-sm text-gray-500 mt-1">Product: {productName}</p>
          )}
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
        <div className="mx-auto max-w-md">
          <ol className="space-y-2 text-sm">
            {steps.map((s, idx) => {
              const isCurrent = idx === currentIdx;
              const isDone = idx < currentIdx;
              return (
                <li key={s.key} className="flex items-center gap-2">
                  <span
                    className={[
                      'inline-flex h-5 w-5 items-center justify-center rounded-full border text-xs',
                      isDone ? 'bg-green-50 border-green-300 text-green-700' : '',
                      isCurrent ? 'bg-blue-50 border-blue-300 text-blue-700' : '',
                      !isDone && !isCurrent ? 'bg-gray-50 border-gray-300 text-gray-600' : ''
                    ].join(' ')}
                  >
                    {isDone ? '✓' : idx + 1}
                  </span>
                  <span className={isCurrent ? 'font-medium text-gray-900' : 'text-gray-700'}>
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Analysis Failed</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-x-4">
            <button 
              onClick={() => analyzeTopics()} 
              className="simpi-button simpi-button-outline"
            >
              Try Again
            </button>
            <button 
              onClick={onBack} 
              className="simpi-button simpi-button-outline"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Avoid briefly showing the "No topics found" screen while results are still being applied
  // (or while a deduped re-run is about to populate topics).
  if (!noTopicsFound && topics.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Analyzing Content</h2>
          <p className="text-gray-600">Processing and ranking suggested topics…</p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Select Topics</h2>
        <div className="mb-3 flex justify-center">
          <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-900">
            {intentLabel} from {sourceLabel}
          </span>
        </div>
        <p className="mx-auto max-w-2xl text-sm text-gray-600">
          {contextSummary}
        </p>
        <div className="mt-3 flex items-center justify-center gap-2 text-sm">
          <span className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-gray-700">
            Language: {LANG_LABEL[sourceLangCode]}
          </span>
          <button
            onClick={openSourceModal}
            className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-800 hover:bg-gray-50"
            type="button"
          >
            Review source
          </button>
          <button
            onClick={() => {
              setPhase('language');
              setTopics([]);
              setSelectedTopics(new Set());
              setNoTopicsFound(false);
              setError(null);
              setLoading(false);
              lastAnalyzeKeyRef.current = null;
              onCacheState?.({ topics: [], selectedTopicIds: [] });
            }}
            className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-800 hover:bg-gray-50"
            type="button"
          >
            Change language
          </button>
        </div>
      </div>

      {showSourceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-5xl rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <div className="text-lg font-semibold">Review source selection</div>
                <div className="text-sm text-gray-600">
                  Choose which sections of the IFU should be used for topic extraction.
                </div>
              </div>
              <button
                onClick={() => setShowSourceModal(false)}
                className="rounded-md px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
                type="button"
              >
                Close
              </button>
            </div>

            <div className="grid grid-cols-1 gap-0 md:grid-cols-2">
              <div className="max-h-[70vh] overflow-auto border-r p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-700">Sections</div>
                  <div className="text-xs text-gray-500">
                    Included: {includedSectionIds.size} / {sections.length}
                  </div>
                </div>

                <div className="space-y-2">
                  {sections.map((s) => {
                    const checked = includedSectionIds.has(s.sectionId);
                    return (
                      <label
                        key={s.sectionId}
                        className="flex cursor-pointer items-start gap-2 rounded-md border p-3 hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSection(s.sectionId)}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="truncate text-sm font-medium text-gray-900">{s.title}</div>
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                              {s.type}
                            </span>
                          </div>
                          <div className="mt-1 text-xs text-gray-600">{s.preview}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="max-h-[70vh] overflow-auto p-4">
                <div className="mb-2 text-sm font-medium text-gray-700">Selected preview</div>
                <div className="space-y-3 text-sm text-gray-800">
                  {sections
                    .filter((s) => includedSectionIds.has(s.sectionId))
                    .slice(0, 30)
                    .map((s) => (
                      <div key={s.sectionId} className="rounded-md border bg-gray-50 p-3">
                        <div className="mb-1 text-xs font-semibold text-gray-700">{s.title}</div>
                        <div className="text-xs text-gray-700">{s.preview}</div>
                      </div>
                    ))}
                  {includedSectionIds.size > 30 && (
                    <div className="text-xs text-gray-500">
                      Preview truncated (showing first 30 included sections).
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t px-6 py-4">
              <button
                onClick={() => setShowSourceModal(false)}
                className="rounded-md border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                type="button"
                disabled={savingSelection}
              >
                Cancel
              </button>
              <button
                onClick={saveSelection}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                type="button"
                disabled={savingSelection || includedSectionIds.size === 0 || !documentTextHash}
              >
                {savingSelection ? 'Saving…' : 'Save & re-run topics'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {Object.entries(groupTopicsByCategory(topics)).map(([category, categoryTopics]) => (
          <div key={category} className="simpi-group-container">
            <h4 className="simpi-group-title">{getCategoryDisplayName(category)}</h4>
            <div className="simpi-grid">
              {categoryTopics.map((topic) => {
                const isSelected = selectedTopics.has(topic.id);
                
                return (
                  <div key={topic.id} className="simpi-item">
                    <div className="simpi-card-wrapper" onClick={() => toggleTopic(topic.id)}>
                      <div className="simpi-card">
                        <div className="simpi-card-text">
                          <div className="topic-card-content">
                            <div className="topic-header">
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 mt-1">
                                  {isSelected ? (
                                    <CheckCircle className="w-5 h-5 text-blue-500" />
                                  ) : (
                                    <Circle className="w-5 h-5 text-gray-400" />
                                  )}
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2">
                                    {getCategoryIcon(topic.category)}
                                    <h3 className="title">{topic.title}</h3>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="topic-description">
                              <p className="brand">{topic.description}</p>
                            </div>
                            
                            <div className="topic-metadata">
                              <div className="flex items-center space-x-2 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(topic.category)}`}>
                                  {topic.category}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {noTopicsFound && topics.length === 0 && (
        <div className="text-center py-8">
          <Book className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Topics Found</h3>
          <p className="text-gray-600 mb-4">
            We couldn't identify any suitable topics from the document content.
          </p>
          <button 
            onClick={onBack} 
            className="simpi-button simpi-button-outline"
          >
            Go Back
          </button>
        </div>
      )}

      <div className="flex justify-between items-center pt-4">
        <button 
          onClick={onBack} 
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-600">
            {selectedTopics.size} of {topics.length} topics selected
          </span>
          <button
            onClick={handleContinue}
            disabled={selectedTopics.size === 0 || isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Preparing...
              </>
            ) : (
              <>
                <Book className="w-4 h-4 mr-2" />
                Continue ({selectedTopics.size})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopicSelection;